import authSession from "../../lib/authSession";
import { API_URL } from "../../constants";
import { safeFetch, explainApiError } from "../../utils/toDisplayString";

export const resourcePersonCourseInitialState = {
  courses: [],
  coursesLoading: false,
  coursesError: null,
  coursesTotal: 0,
  coursesPages: 1,
  courseDetail: null,
  courseDetailLoading: false,
  courseDetailError: null,
  courseStudents: [],
  courseStudentsLoading: false,
  courseStudentsError: null,
  courseStudentsTotal: 0,
  courseStudentsPages: 1,
  courseAnalytics: null,
  courseAnalyticsLoading: false,
  courseAnalyticsError: null,
  courseMutationLoading: false,
  courseMutationError: null,
};

function buildUrl(path, params) {
  const query = new URLSearchParams(
    Object.entries(params || {}).filter(([, value]) => value !== undefined && value !== "")
  ).toString();
  return `${API_URL.replace(/\/$/, "")}${path}${query ? `?${query}` : ""}`;
}

function requestOptions(get, method = "GET", body) {
  const token = authSession.getToken();
  const headers = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;
  return {
    method,
    headers,
    credentials: "omit",
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };
}

async function request(get, path, options = {}) {
  if (!API_URL) throw new Error("REACT_APP_API_BASE_URL is not configured.");
  const response = await safeFetch(buildUrl(path, options.params), requestOptions(get, options.method, options.body));
  const text = await response.text();
  const contentType = response.headers.get("content-type") || "";
  let data = {};

  if (text) {
    if (contentType.includes("application/json")) {
      data = JSON.parse(text);
    } else if (text.trim().startsWith("<")) {
      throw new Error(`Resource person courses API returned HTML (${response.status}) for ${response.url}.`);
    } else {
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(`Resource person courses API returned a non-JSON response (${response.status}).`);
      }
    }
  }

  if (!response.ok) {
    // Route through explainApiError so authors see a plain instruction (e.g. "only
    // draft courses can be edited" becomes "withdraw it first if you need to make
    // changes") instead of raw backend text, and an empty body still yields a
    // useful per-status sentence rather than "Request failed with status 409."
    const err = new Error(explainApiError(response.status, data, "We couldn't save your changes."));
    err.status = response.status;
    err.payload = data;
    throw err;
  }
  return data;
}

/**
 * Unwrap the created/updated course from any of the envelopes the API uses:
 * `{ id, … }` · `{ course: { id, … } }` · `{ data: { id, … } }` ·
 * `{ data: { course: { id, … } } }`. Stopping after a single unwrap made
 * `{ data: { course: … } }` return the envelope, so callers saw `id === undefined`
 * and the new id was lost (`savedShellId` never flipped).
 */
function payloadOf(data) {
  const ENTITY_KEYS = ["course", "lesson", "question", "category", "module", "item"];
  let value = data;
  for (let depth = 0; depth < 3; depth += 1) {
    if (!value || typeof value !== "object" || Array.isArray(value)) break;
    if (value.data && typeof value.data === "object" && !Array.isArray(value.data)) {
      value = value.data;
      continue;
    }
    // Only unwrap while we have no entity yet: a course that already carries an
    // id IS the entity, even when it also nests a `category` object.
    if (value.id === undefined && value._id === undefined) {
      const key = ENTITY_KEYS.find(
        (k) => value[k] && typeof value[k] === "object" && !Array.isArray(value[k])
      );
      if (key) {
        value = value[key];
        continue;
      }
    }
    break;
  }
  return value && typeof value === "object" ? value : {};
}

function listOf(data, keys) {
  const payload = payloadOf(data);
  for (const key of keys) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  return Array.isArray(payload) ? payload : [];
}

function paginationOf(data, items) {
  const payload = payloadOf(data);
  return {
    items,
    total: payload.total ?? payload.totalItems ?? items.length,
    totalPages: payload.totalPages ?? payload.pages ?? 1,
  };
}

export function createResourcePersonCourseActions(set, get) {
  const setError = (prefix, error, fields) => {
    set({ ...fields, [`${prefix}Loading`]: false, [`${prefix}Error`]: error.message });
    // `status` must be carried through: the builder keys off it to flip into the
    // read-only state when the server rejects an edit with 409.
    return { success: false, error: error.message, status: error.status };
  };

  return {
    fetchCourses: async (params = {}) => {
      set({ coursesLoading: true, coursesError: null });
      try {
        const data = await request(get, "/api/resource-person/courses", { params });
        const result = paginationOf(data, listOf(data, ["items", "courses"]));
        set({ coursesLoading: false, courses: result.items, coursesTotal: result.total, coursesPages: result.totalPages });
        return { success: true, ...result };
      } catch (error) {
        return setError("courses", error, { courses: [], coursesTotal: 0 });
      }
    },

    createCourse: async (payload) => {
      set({ courseMutationLoading: true, courseMutationError: null });
      try {
        const data = await request(get, "/api/resource-person/courses", { method: "POST", body: payload });
        set({ courseMutationLoading: false });
        return { success: true, course: payloadOf(data) };
      } catch (error) {
        return setError("courseMutation", error, {});
      }
    },

    fetchCourseById: async (id) => {
      set({ courseDetailLoading: true, courseDetailError: null });
      try {
        const data = await request(get, `/api/resource-person/courses/${id}`);
        const course = payloadOf(data);
        set({ courseDetailLoading: false, courseDetail: course });
        return { success: true, course };
      } catch (error) {
        return setError("courseDetail", error, { courseDetail: null });
      }
    },

    updateCourse: async (id, payload) => {
      set({ courseMutationLoading: true, courseMutationError: null });
      try {
        const data = await request(get, `/api/resource-person/courses/${id}`, { method: "PATCH", body: payload });
        set({ courseMutationLoading: false });
        return { success: true, course: payloadOf(data) };
      } catch (error) {
        return setError("courseMutation", error, {});
      }
    },

    submitCourse: async (id) => {
      return requestMutation(set, get, `/api/resource-person/courses/${id}/submit`);
    },

    unsubmitCourse: async (id) => {
      return requestMutation(set, get, `/api/resource-person/courses/${id}/unsubmit`);
    },

    deleteCourse: async (id) => {
      return requestMutation(set, get, `/api/resource-person/courses/${id}`, "DELETE");
    },

    fetchCourseStudents: async (id, params = {}) => {
      set({ courseStudentsLoading: true, courseStudentsError: null });
      try {
        const data = await request(get, `/api/resource-person/courses/${id}/students`, { params });
        const result = paginationOf(data, listOf(data, ["items", "students"]));
        set({ courseStudentsLoading: false, courseStudents: result.items, courseStudentsTotal: result.total, courseStudentsPages: result.totalPages });
        return { success: true, ...result };
      } catch (error) {
        return setError("courseStudents", error, { courseStudents: [], courseStudentsTotal: 0 });
      }
    },

    fetchCourseAnalytics: async (id) => {
      set({ courseAnalyticsLoading: true, courseAnalyticsError: null });
      try {
        const data = await request(get, `/api/resource-person/courses/${id}/analytics`);
        const analytics = payloadOf(data);
        set({ courseAnalyticsLoading: false, courseAnalytics: analytics });
        return { success: true, analytics };
      } catch (error) {
        return setError("courseAnalytics", error, { courseAnalytics: null });
      }
    },
  };
}

async function requestMutation(set, get, path, method = "PATCH") {
  set({ courseMutationLoading: true, courseMutationError: null });
  try {
    const data = await request(get, path, { method });
    set({ courseMutationLoading: false });
    return { success: true, course: payloadOf(data) };
  } catch (error) {
    return setMutationError(set, error);
  }
}

function setMutationError(set, error) {
  set({ courseMutationLoading: false, courseMutationError: error.message });
  return { success: false, error: error.message, status: error.status };
}
